import React, { useEffect, useRef } from 'react'
import { IonLabel, IonTextarea } from '@ionic/react'
import './ExpandableTextarea.component.css'

export interface IExpandableTextareaProps {
  dataTestId: string
  value: string
  maxLength: number
  onChange: (value: string) => void
  label?: string
  rowsCollapsed?: number
  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  className?: string
  inputClassName?: string
}

const ExpandableTextarea: React.FC<IExpandableTextareaProps> = ({
  dataTestId,
  value,
  maxLength,
  onChange,
  label,
  rowsCollapsed = 3,
  isExpanded = false,
  onExpandedChange,
  className,
  inputClassName,
}) => {
  const textareaRef = useRef<HTMLIonTextareaElement>(null)

  useEffect(() => {
    if (!isExpanded) (document.activeElement as HTMLElement | null)?.blur()
  }, [isExpanded])

  const handleFocus = async () => {
    onExpandedChange?.(true)

    const input = await textareaRef.current?.getInputElement()
    if (input) {
      const cursorPosition = input.value.length
      input.setSelectionRange(cursorPosition, cursorPosition)
    }
  }

  const handleChange = (e: CustomEvent) => {
    onChange(e.detail.value || '')
  }

  const handleLabelClick = (event: React.MouseEvent) => {
    if (!isExpanded) return
    event.stopPropagation()
    onExpandedChange?.(false)
  }

  return (
    <div className={`expandable-textarea ${isExpanded ? 'expandable-textarea-expanded' : ''} ${className || ''}`}>
      {isExpanded && (
        <div
          className='expandable-textarea-backdrop'
          onPointerDown={() => onExpandedChange?.(false)}
          aria-hidden='true'
        />
      )}
      {label ? (
        <IonLabel
          color='dark'
          className='expandable-textarea-label fw-700 font-size-16 cursor-pointer'
          onClick={handleLabelClick}>
          {label}
        </IonLabel>
      ) : null}

      <div
        className={`expandable-textarea-input ${isExpanded ? 'expandable-textarea-input-expanded' : ''} ${
          inputClassName || ''
        }`}>
        <IonTextarea
          ref={textareaRef}
          data-testid={dataTestId}
          className='expandable-textarea-field'
          value={value}
          rows={isExpanded ? 1 : rowsCollapsed}
          maxlength={maxLength}
          color='primary-alt'
          autocapitalize='sentences'
          onIonFocus={handleFocus}
          onIonChange={handleChange}
        />

        <div className='expandable-textarea-counter-row'>
          <h1 className={value?.length < maxLength ? 'text-color-blue-tint' : 'text-color-red'}>
            {value?.length}/{maxLength}
          </h1>
        </div>
      </div>
    </div>
  )
}

export default ExpandableTextarea
